<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({ modelValue: String, disabled: Boolean })
const emit = defineEmits(['update:modelValue'])
const trigger = ref(null)
const board = ref(null)
const open = ref(false)
const hue = ref(0)
const saturation = ref(0)
const brightness = ref(100)
const hex = ref('#FFFFFF')
const panelStyle = ref({})

function normalize(value) {
  let result = String(value || '').trim().toUpperCase()
  if (!result.startsWith('#')) result = `#${result}`
  return /^#[0-9A-F]{6}$/.test(result) ? result : '#FFFFFF'
}
function hexToHsv(value) {
  const normalized = normalize(value)
  const r = parseInt(normalized.slice(1, 3), 16) / 255
  const g = parseInt(normalized.slice(3, 5), 16) / 255
  const b = parseInt(normalized.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  let h = 0
  if (delta) {
    if (max === r) h = 60 * (((g - b) / delta) % 6)
    else if (max === g) h = 60 * ((b - r) / delta + 2)
    else h = 60 * ((r - g) / delta + 4)
  }
  return { h: h < 0 ? h + 360 : h, s: max ? delta / max : 0, v: max }
}
function hsvToHex(h, s, v) {
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c
  let [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x]
  return `#${[r, g, b].map((channel) => Math.round((channel + m) * 255).toString(16).padStart(2, '0')).join('')}`.toUpperCase()
}
function sync(value) {
  hex.value = normalize(value)
  const hsv = hexToHsv(hex.value)
  hue.value = hsv.h
  saturation.value = hsv.s
  brightness.value = hsv.v
}
function commit() {
  hex.value = hsvToHex(hue.value, saturation.value, brightness.value)
  emit('update:modelValue', hex.value)
}
function setHue(event) {
  hue.value = Number(event.target.value)
  commit()
}
function moveBoard(event) {
  if (!board.value) return
  const rect = board.value.getBoundingClientRect()
  saturation.value = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
  brightness.value = 1 - Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
  commit()
}
function startBoard(event) {
  moveBoard(event)
  window.addEventListener('pointermove', moveBoard)
  window.addEventListener('pointerup', stopBoard, { once: true })
}
function stopBoard() {
  window.removeEventListener('pointermove', moveBoard)
}
function setHex(event) {
  const value = normalize(event.target.value)
  sync(value)
  emit('update:modelValue', value)
}
function placePanel() {
  const rect = trigger.value?.getBoundingClientRect()
  if (!rect) return
  const width = 268
  const left = Math.min(window.innerWidth - width - 12, Math.max(12, rect.left))
  const top = rect.bottom + 8 + 320 > window.innerHeight ? Math.max(12, rect.top - 328) : rect.bottom + 8
  panelStyle.value = { left: `${left}px`, top: `${top}px`, width: `${width}px` }
}
async function toggle() {
  if (props.disabled) return
  open.value = !open.value
  if (open.value) {
    await nextTick()
    placePanel()
  }
}
function outside(event) {
  if (open.value && !event.target.closest?.('.nr-color-panel') && !trigger.value?.contains(event.target)) open.value = false
}

watch(() => props.modelValue, sync, { immediate: true })
document.addEventListener('pointerdown', outside)
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', outside)
  stopBoard()
})

const pureHue = computed(() => `hsl(${hue.value} 100% 50%)`)
const markerStyle = computed(() => ({ left: `${saturation.value * 100}%`, top: `${(1 - brightness.value) * 100}%` }))
</script>

<template><div class="nr-color-picker">
    <button ref="trigger" type="button" class="nr-color-trigger" :style="{ background: hex }" :disabled="disabled" :aria-expanded="open" aria-haspopup="dialog" aria-label="Choose color" @click="toggle" />
    <span class="nr-color-value">{{ hex }}</span>
    <Teleport to="body">
      <div v-if="open" class="nr-color-panel" :style="panelStyle" role="dialog" aria-label="Color picker">
        <div ref="board" class="nr-color-board" :style="{ backgroundColor: pureHue }" @pointerdown.prevent="startBoard">
          <span class="nr-color-marker" :style="markerStyle" />
        </div>
        <input class="nr-color-hue" type="range" min="0" max="360" :value="hue" aria-label="Hue" @input="setHue">
        <div class="nr-color-input-row">
          <span class="nr-color-preview" :style="{ background: hex }" />
          <input :value="hex" class="nr-color-hex-input" maxlength="7" spellcheck="false" autocomplete="off" @change="setHex">
        </div>
      </div>
    </Teleport>
  </div></template>

<style scoped>
.nr-color-picker { display: flex; align-items: center; gap: 10px; }
.nr-color-trigger { width: 38px; height: 38px; padding: 0; border: 3px solid var(--nr-dialog-line); border-radius: 10px; box-shadow: inset 0 0 0 2px var(--nr-dialog-bg); }
.nr-color-value { color: var(--nr-dialog-mute); font: 700 .78rem/1 monospace; }
.nr-color-panel { position: fixed; z-index: 30000; padding: 12px; border: 1px solid var(--nr-overlay-line); border-radius: 12px; background: var(--nr-overlay-bg); box-shadow: var(--nr-overlay-shadow); }
.nr-color-board { position: relative; height: 210px; overflow: hidden; border-radius: 9px; cursor: crosshair; background-image: linear-gradient(to top,#000,transparent),linear-gradient(to right,#fff,transparent); }
.nr-color-marker { position: absolute; width: 14px; height: 14px; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 1px 4px #000; transform: translate(-50%,-50%); pointer-events: none; }
.nr-color-hue { width: 100%; height: 18px; margin: 12px 0 6px; appearance: none; border-radius: 999px; background: linear-gradient(to right,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00); }
.nr-color-hue::-webkit-slider-thumb { width: 16px; height: 22px; appearance: none; border: 2px solid #fff; border-radius: 5px; background: transparent; box-shadow: 0 1px 4px #000; }
.nr-color-input-row { display: flex; align-items: center; gap: 8px; }
.nr-color-preview { width: 32px; height: 32px; border: 1px solid var(--nr-overlay-line); border-radius: 8px; }
.nr-color-hex-input { flex: 1; min-width: 0; padding: 8px 10px; border: 1px solid var(--nr-overlay-line); border-radius: 8px; background: var(--nr-overlay-surface); color: var(--nr-overlay-text); font: 700 .85rem monospace; }
</style>
